import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from '@/lib/dayjs'
import {
  getGetApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEventsQueryKey,
  useDeleteApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEventsEventId,
  usePatchApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEventsEventId,
  usePatchApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEventsEventIdIcon,
  usePostApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEvents,
} from '@/api/generated/company-management-user/company-management-user'
import type {
  CalendarServiceModuleEventModel,
  CreateCalendarServiceModuleEventModel,
} from '@/api/model'
import { LocalizationEntityType } from '@/api/model/localizationEntityType'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Textarea } from '@/components/shadcn/textarea'
import { Label } from '@/components/shadcn/label'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Separator } from '@/components/shadcn/separator'
import { IconColorPicker } from '@/components/common/IconColorPicker'
import { GroupMultiSelect } from '@/components/common/GroupMultiSelect'
import { TranslateButton } from '@/components/common/translations/TranslateButton'
import { useEntityTranslations } from '@/components/common/translations/useEntityTranslations'
import { notify } from '@/lib/notify'
import { ReminderNotificationsPicker } from './ReminderNotificationsPicker'
import { EventSurveyDetails } from './EventSurveyDetails'
import {triggerAutoTranslation, useResolvedLanguages} from "@/components/common/translations/useAutoTranslate.ts";
import {AutoTranslateAllButton} from "@/components/common/translations/AutoTranslateAllButton.tsx";
import type {PlannedLanguages} from "@/components/common/translations/types.ts";

const DEFAULT_COLOR = '#43B8BB'
const TRANSLATABLE_PROPERTIES = ['title', 'description']

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  serviceModuleId: number
  hasSurveyAppointmentSubtypes: boolean
  /** Prefill: existing event (with `id`) = edit, otherwise create. */
  event: Partial<CalendarServiceModuleEventModel>
  /** Copies the currently opened event into a new one (create mode). */
  onCopy: () => void
}

interface FormState {
  title: string
  description: string
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  hasTime: boolean
  hasEndDate: boolean
  iconName: string
  colorHex: string
  iconUrl: string | null
  isCompanyWideAssigned: boolean
  assignedGroupIds: number[]
  reminderNotifications: string[]
  sendUpdateNotification: boolean
  surveyAppointmentAllowed: boolean
  capacity: number | null
  surveyAppointmentReward: number | null
}

function seedForm(event: Partial<CalendarServiceModuleEventModel>): FormState {
  // Backend stores date values as UTC wall-clock time (the display uses utcOffset(0)) —
  // so read them as UTC when filling in, otherwise day/time would be shifted.
  const start = event.startDate ? dayjs.utc(event.startDate) : dayjs.utc().startOf('day')
  const end = event.endDate ? dayjs.utc(event.endDate) : start
  return {
    title: event.title ?? '',
    description: event.description ?? '',
    startDate: start.format('YYYY-MM-DD'),
    startTime: start.format('HH:mm'),
    endDate: end.format('YYYY-MM-DD'),
    endTime: end.format('HH:mm'),
    hasTime: event.hasTime === true,
    hasEndDate: !!event.startDate && !!event.endDate && event.startDate !== event.endDate,
    iconName: event.iconName ?? 'circle',
    colorHex: event.colorHex ?? DEFAULT_COLOR,
    iconUrl: event.iconUrl ?? null,
    isCompanyWideAssigned: event.isCompanyWideAssigned !== false,
    assignedGroupIds: event.assignedGroupIds ?? [],
    reminderNotifications: event.reminderNotifications ?? [],
    sendUpdateNotification: event.sendUpdateNotification ?? false,
    surveyAppointmentAllowed: event.surveyAppointmentAllowed ?? false,
    capacity: event.capacity ?? null,
    surveyAppointmentReward: event.surveyAppointmentReward ?? null,
  }
}

/** Create / edit / copy / delete an appointment (parity with the old CalendarEventModal). */
export function CalendarEventDialog({
  open,
  onOpenChange,
  serviceModuleId,
  hasSurveyAppointmentSubtypes,
  event,
  onCopy,
}: Props) {
  const { t } = useTranslation(['service-modules-calendar', 'common'])
  const queryClient = useQueryClient()
  const eventId = event.id
  const isEdit = eventId != null

  const languages = useResolvedLanguages()
  const [autoTranslateAll, setAutoTranslateAll] = useState(false)
  const [titlePlanned, setTitlePlanned] = useState<PlannedLanguages>(new Set())
  const [descriptionPlanned, setDescriptionPlanned] = useState<PlannedLanguages>(new Set())
  const [form, setForm] = useState<FormState>(() => seedForm(event))

  // Show the title error (red border) only after interaction, not already on open.
  const [titleTouched, setTitleTouched] = useState(false)
  const patch = (p: Partial<FormState>) => setForm((prev) => ({ ...prev, ...p }))

  const translations = useEntityTranslations({
    entityType: LocalizationEntityType.CalendarEvent,
    entityId: isEdit ? String(eventId) : undefined,
    properties: TRANSLATABLE_PROPERTIES,
    enabled: open,
  })

  const create = usePostApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEvents()
  const update = usePatchApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEventsEventId()
  const remove = useDeleteApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEventsEventId()
  const patchIcon = usePatchApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEventsEventIdIcon()

  // Re-fill on every open (or change of the event, e.g. through copying).
  useEffect(() => {
    if (open) {
      setForm(seedForm(event))
      setTitleTouched(false)
      translations.reset()
      setAutoTranslateAll(false)
      setTitlePlanned(new Set())
      setDescriptionPlanned(new Set())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event])

  const invalidateEvents = () =>
    queryClient.invalidateQueries({
      queryKey: getGetApiCompaniesCurrentServicemodulesCalendarsServiceModuleIdEventsQueryKey(serviceModuleId),
    })

  const busy = create.isPending || update.isPending || remove.isPending
  const saveDisabled = form.title.trim() === '' || busy

  function buildPayload(): CreateCalendarServiceModuleEventModel {
    const startDay = dayjs(form.startDate)
    const applyTime = (day: dayjs.Dayjs, time: string) => {
      const [h, m] = time.split(':').map(Number)
      return day.hour(h).minute(m).second(0)
    }
    const start = form.hasTime ? applyTime(startDay, form.startTime) : startDay.startOf('day')
    const endDay = dayjs(form.endDate)
    const end = form.hasEndDate
      ? form.hasTime
        ? applyTime(endDay, form.endTime)
        : endDay.startOf('day')
      : start

    return {
      title: form.title,
      description: form.description,
      iconName: form.iconName,
      colorHex: form.colorHex,
      // utc(true): write the local wall-clock time unchanged as UTC (like the old portal).
      startDate: start.utc(true).toISOString(),
      endDate: end.utc(true).toISOString(),
      hasTime: form.hasTime,
      isCompanyWideAssigned: form.isCompanyWideAssigned,
      assignedGroupIds: form.assignedGroupIds,
      reminderNotifications: form.reminderNotifications,
      sendUpdateNotification: form.sendUpdateNotification,
      capacity: form.capacity,
      surveyAppointmentAllowed: form.surveyAppointmentAllowed,
      surveyAppointmentReward: form.surveyAppointmentReward,
    }
  }

  async function handleSave() {
    const payload = buildPayload()
    try {
      let savedId: number
      if (isEdit) {
        await update.mutateAsync({ serviceModuleId, eventId, data: payload })
        savedId = eventId
      } else {
        const res = await create.mutateAsync({ serviceModuleId, data: payload })
        savedId = (res.data as CalendarServiceModuleEventModel).id
      }
      await translations.save(String(savedId))
      const allLanguageCodes = languages.map((l) => l.code)
      triggerAutoTranslation({
        entityType: 'CalendarEvent',
        entityId: String(savedId),
        properties: [
          {
            property: 'title',
            plannedLanguages: autoTranslateAll ? allLanguageCodes : [...titlePlanned],
            existingTranslations: translations.get('title'),
          },
          {
            property: 'description',
            plannedLanguages: autoTranslateAll ? allLanguageCodes : [...descriptionPlanned],
            existingTranslations: translations.get('description'),
          },
        ],
      })
      await invalidateEvents()
      notify.success(isEdit ? t('event.toasts.updated') : t('event.toasts.created'))
      onOpenChange(false)
    } catch (error) {
      notify.apiError(error, t('event.toasts.saveError'))
    }
  }

  async function handleDelete() {
    if (!isEdit) return
    try {
      await remove.mutateAsync({ serviceModuleId, eventId })
      await invalidateEvents()
      notify.success(t('event.toasts.deleted'))
      onOpenChange(false)
    } catch (error) {
      notify.apiError(error, t('event.toasts.deleteError'))
    }
  }

  // Icon upload only in the edit case (needs the event id). Backend expects the
  // file as multipart FormData (`File`); sending `File: null` clears/removes it.
  async function uploadIcon(file: File, dataUrl: string) {
    if (!isEdit) return
    try {
      await patchIcon.mutateAsync({ serviceModuleId, eventId, data: { File: file } })
      patch({ iconUrl: dataUrl })
      await invalidateEvents()
    } catch (error) {
      notify.apiError(error, t('event.toasts.iconError'))
    }
  }

  async function deleteIcon() {
    if (!isEdit) return
    try {
      await patchIcon.mutateAsync({ serviceModuleId, eventId, data: { File: undefined } })
      patch({ iconUrl: null })
      await invalidateEvents()
    } catch (error) {
      notify.apiError(error, t('event.toasts.iconError'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-4xl! flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('event.editTitle') : t('event.createTitle')}</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto pr-2">
          <div className="flex flex-col gap-6 py-1 md:flex-row">
            {/* Column 1: appointment details */}
            <div className="flex flex-1 flex-col gap-4">
              <p className="text-sm font-medium">{t('event.details')}</p>

              <IconColorPicker
                iconName={form.iconName}
                colorHex={form.colorHex}
                iconUrl={form.iconUrl}
                busy={patchIcon.isPending}
                uploadDisabled={!isEdit}
                onIconChange={(iconName) => patch({ iconName })}
                onColorChange={(colorHex) => patch({ colorHex })}
                onUpload={uploadIcon}
                onDelete={deleteIcon}
              />

              <div className="space-y-1.5">
                <Label htmlFor="event-title">{t('event.fields.title')} *</Label>
                <div className="flex gap-2">
                  <Input
                    id="event-title"
                    value={form.title}
                    aria-invalid={titleTouched && form.title.trim() === ''}
                    onBlur={() => setTitleTouched(true)}
                    onChange={(e) => patch({ title: e.target.value })}
                  />
                  <TranslateButton
                    dialogTitle={t('event.fields.title')}
                    value={translations.get('title')}
                    onChange={(v) => translations.set('title', v)}
                    plannedLanguages={titlePlanned}
                    onPlannedChange={setTitlePlanned}
                    autoTranslateAll={autoTranslateAll}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="event-description">{t('event.fields.description')}</Label>
                <div className="flex gap-2">
                  <Textarea
                    id="event-description"
                    rows={2}
                    value={form.description}
                    onChange={(e) => patch({ description: e.target.value })}
                  />
                  <TranslateButton
                    dialogTitle={t('event.fields.description')}
                    value={translations.get('description')}
                    onChange={(v) => translations.set('description', v)}
                    plannedLanguages={descriptionPlanned}
                    onPlannedChange={setDescriptionPlanned}
                    autoTranslateAll={autoTranslateAll}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="event-start-date">{t('event.fields.startDate')}</Label>
                  <Input
                    id="event-start-date"
                    type="date"
                    value={form.startDate}
                    max={form.hasEndDate ? form.endDate : undefined}
                    onChange={(e) => patch({ startDate: e.target.value })}
                  />
                </div>
                {form.hasTime && (
                  <div className="flex-1 space-y-1.5">
                    <Label htmlFor="event-start-time">{t('event.fields.startTime')}</Label>
                    <Input
                      id="event-start-time"
                      type="time"
                      value={form.startTime}
                      onChange={(e) => patch({ startTime: e.target.value })}
                    />
                  </div>
                )}
              </div>

              {form.hasEndDate && (
                <div className="flex gap-3">
                  <div className="flex-1 space-y-1.5">
                    <Label htmlFor="event-end-date">{t('event.fields.endDate')}</Label>
                    <Input
                      id="event-end-date"
                      type="date"
                      value={form.endDate}
                      min={form.startDate}
                      onChange={(e) => patch({ endDate: e.target.value })}
                    />
                  </div>
                  {form.hasTime && (
                    <div className="flex-1 space-y-1.5">
                      <Label htmlFor="event-end-time">{t('event.fields.endTime')}</Label>
                      <Input
                        id="event-end-time"
                        type="time"
                        value={form.endTime}
                        onChange={(e) => patch({ endTime: e.target.value })}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={form.hasEndDate}
                    onCheckedChange={(v) => patch({ hasEndDate: v === true })}
                  />
                  <span className="text-sm">{t('event.fields.alternativeEnd')}</span>
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={form.hasTime}
                    onCheckedChange={(v) => patch({ hasTime: v === true })}
                  />
                  <span className="text-sm">{t('event.fields.hasTime')}</span>
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={form.sendUpdateNotification}
                    onCheckedChange={(v) => patch({ sendUpdateNotification: v === true })}
                  />
                  <span className="text-sm">{t('event.fields.sendUpdateNotification')}</span>
                </label>
              </div>
            </div>

            <Separator orientation="vertical" className="hidden h-auto md:block" />

            {/* Column 2: reminders, assignment, survey */}
            <div className="flex flex-1 flex-col gap-4">
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium">{t('event.reminders.title')}</p>
                <ReminderNotificationsPicker
                  hasTime={form.hasTime}
                  reminders={form.reminderNotifications}
                  onChange={(reminderNotifications) => patch({ reminderNotifications })}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={form.isCompanyWideAssigned}
                    onCheckedChange={(v) => patch({ isCompanyWideAssigned: v === true })}
                  />
                  <span className="text-sm">{t('event.fields.companyWide')}</span>
                </label>
                {!form.isCompanyWideAssigned && (
                  <GroupMultiSelect
                    selectedIds={form.assignedGroupIds}
                    onChange={(assignedGroupIds) => patch({ assignedGroupIds })}
                  />
                )}
              </div>

              {hasSurveyAppointmentSubtypes && (
                <>
                  <Separator />
                  <EventSurveyDetails
                    serviceModuleId={serviceModuleId}
                    eventId={eventId}
                    value={{
                      surveyAppointmentAllowed: form.surveyAppointmentAllowed,
                      capacity: form.capacity,
                      surveyAppointmentReward: form.surveyAppointmentReward,
                    }}
                    onChange={patch}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-end gap-2">
          <AutoTranslateAllButton
            properties={[
              {
                property: 'title',
                sourceValue: form.title,
                existingTranslations: {},
                manualTranslations: translations.get('title'),
              },
              {
                property: 'description',
                sourceValue: form.description,
                existingTranslations: {},
                manualTranslations: translations.get('description'),
              },
            ]}
            autoTranslateAll={autoTranslateAll}
            onAutoTranslateAllChange={setAutoTranslateAll}
          />
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              {t('buttons.cancel', { ns: 'common' })}
            </Button>
            <Button onClick={handleSave} disabled={saveDisabled}>
              {t('buttons.save', { ns: 'common' })}
            </Button>
            {isEdit && (
              <>
                <Button variant="ghost" onClick={onCopy} disabled={busy}>
                  {t('event.copy')}
                </Button>
                <Button variant="destructive" onClick={handleDelete} disabled={busy}>
                  {t('buttons.delete', { ns: 'common' })}
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
